<template>
  <q-layout
    view="lhr lpR lfr"
    :style="styles"
  >
    <div
      v-if="settings.trackingCodes?.headerCode"
      v-html="settings.trackingCodes?.headerCode"
    />
    <q-page-container :class="['blog-page', { 'mobile': isSmallScreen }]">
      <q-page>
        <component :is="renderTemplate" />
      </q-page>
    </q-page-container>
    <q-footer :class="['blog-footer q-py-lg', { 'blog-footer-mobile': isSmallScreen }]">
      <div :class="['blog-container footer q-px-lg', { 'row items-center justify-end': !isSmallScreen }]">
        <div :class="[
          { 'col q-mr-xl flex content-center': !isSmallScreen },
          { 'q-mb-md flex justify-between items-center': isSmallScreen },
        ]">
          <img
            v-if="logo"
            class="cursor-pointer"
            :src="logo"
            :alt="settings.metaFields.metaTitle"
            :width="footerLogoAttributes.width"
            :height="footerLogoAttributes.height"
            @click="router.push({ name: ROUTES.RENDER.BLOG })"
          >

          <Watermark
            v-if="showWatermark && isSmallScreen"
            small
          />
        </div>
        <div :class="[{ 'col-auto flex justify-xs-center justify-sm-end q-ml-xl': !isSmallScreen }]">
          <q-item
            v-for="(item, index) in settings.metaFields.footerLinks"
            :key="index"
            clickable
            :href="item.url || item.publicUrl"
            :target="item.type === 'external' ? '_blank' : '_self'"
            :rel="getRel(item)"
            :class="['footer-link', { 'footer-link-mobile q-pa-none fs-12': isSmallScreen }]"
          >
            <q-item-section>
              {{ item.title }}
            </q-item-section>
          </q-item>
        </div>
        <Watermark
          v-if="showWatermark && !isSmallScreen"
          class="footer-watermark"
        />
      </div>
    </q-footer>
    <div
      v-if="settings.trackingCodes?.footerCode"
      v-html="settings.trackingCodes?.footerCode"
    />
  </q-layout>

  <EmailSubscribeDialog
    v-if="showEmailSubscribeDialog"
    :style="styles"
    :email="emailForSubscribe"
    @hide="showEmailSubscribeDialog = false"
  />
</template>
<script setup>
import { useRenderStore } from 'stores/render';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { ROUTES } from 'src/enums';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import { useFavicon } from '@vueuse/core';

import de from 'quasar/lang/de-DE';
import en from 'quasar/lang/en-US';
import it from 'quasar/lang/it';
import es from 'quasar/lang/es';
import ro from 'quasar/lang/ro';
import pl from 'quasar/lang/pl';
import fr from 'quasar/lang/fr';
import EmailSubscribeDialog from 'components/dialog/EmailSubscribeDialog.vue';
import BasicLayout from 'components/render/BasicLayout.vue';
import FeedLayout from 'components/render/FeedLayout.vue';
import ColumnLayout from 'components/render/ColumnLayout.vue';
import Watermark from 'components/Watermark.vue';
import useRender from 'src/composables/useRender';

defineOptions({
  // eslint-disable-next-line consistent-return
  async preFetch({ ssrContext, store }) {
    const renderStore = useRenderStore(store);
    try {
      return await Promise.all([
        renderStore.getPosts(ssrContext.req.hostname),
        renderStore.getBlogSettings(ssrContext.req.hostname),
      ]);
    } catch (error) {
      console.log(error);
    }
  },
});

const $q = useQuasar();
const renderStore = useRenderStore();
const router = useRouter();
const { locale } = useI18n();

const {
  settings,
  showEmailSubscribeDialog,
  emailForSubscribe,
} = storeToRefs(renderStore);

const {
  isSmallScreen,
  logo,
  footerLogoAttributes,
} = useRender();

const renderTemplate = computed(() => ({
  basic: BasicLayout,
  feed: FeedLayout,
  column: ColumnLayout,
}[settings.value.metaFields.templateLayout]));
const showWatermark = computed(() => !settings.value.metaFields?.removeBranding);

const favicon = useFavicon();

if (settings.value.metaFields?.faviconGuid?.url) {
  favicon.value = settings.value.metaFields.faviconGuid.url;
}

locale.value = settings.value.language.locale;

const lang = {
  en_US: en,
  de_DE: de,
  it_IT: it,
  fr_FR: fr,
  es_ES: es,
  ro_RO: ro,
  pl_PL: pl,
};

$q.lang.set(lang[locale.value]);

const styles = computed(() => (`
  --blog-font-color: ${settings.value.metaFields.fontColor};
  --blog-primary-color: ${settings.value.metaFields.primaryColor};
  --blog-icon-color: ${settings.value.metaFields.iconColor};
  --blog-border-color: ${settings.value.metaFields.borderColor};
  --blog-card-color: ${settings.value.metaFields.cardColor};
  --blog-background-color: ${settings.value.metaFields.backgroundColor};
`));

function getRel(item) {
  let relText = '';
  if (item.metaFields) {
    if (item.metaFields.nofollow) {
      relText += 'nofollow ';
    }

    if (item.metaFields.noopener) {
      relText += 'noopener ';
    }

    if (item.metaFields.noreferrer) {
      relText += 'noreferrer ';
    }

    return relText.length ? relText.trim() : undefined;
  }

  if (item.nofollow) {
    relText += 'nofollow ';
  }

  if (item.noopener) {
    relText += 'noopener ';
  }

  if (item.noreferrer) {
    relText += 'noreferrer ';
  }

  return relText.length ? relText.trim() : undefined;
}
</script>

<style>
body {
  font-family: -apple-system, "system-ui", "Segoe UI", "Trebuchet MS", Arial, sans-serif;
}
</style>

<style lang="scss" scoped>
.blog-container {
  max-width: 1600px;
  color: var(--blog-font-color, #000);
  margin: 0 auto;

  &.blog-container-mobile {
    flex-direction: column;
  }
}

.blog-sidebar {
  min-width: 290px;
}

.blog-header,
.blog-sidebar,
.blog-page,
.blog-footer {
  background: var(--blog-background-color, $grey-1);
}

.logo,
.sidebar-content {
  position: sticky;
  top: 16px;
}

.blog-banner {
  max-width: 290px;
}

:deep(.q-drawer) {
  background: none;
}

:deep(.blog-page) {
  font-size: 14px;
  font-weight: 700;
  line-height: 26px;

  .q-card__section--vert {
    padding-top: 0;
    padding-bottom: 0;
  }
  h2 {
    font-size: 25px;
    font-weight: 700;
    line-height: 37px;
  }
  h3 {
    font-size: 21px;
    font-weight: 700;
    line-height: 37px;
  }
  h4 {
    font-size: 18px;
    font-weight: 700;
    line-height: 37px;
  }
  h5 {
    font-size: 16px;
    font-weight: 700;
    line-height: 37px;
  }

  strong {
    font-weight: 900;
  }

  &.mobile {
    padding-bottom: 0 !important;

    h1 {
      font-size: 24px !important;
      line-height: 32px !important;
    }
    h2 {
      font-size: 22px;
      line-height: 32px;
    }
    h3 {
      font-size: 20px;
      line-height: 30px;
    }
    h4 {
      font-size: 18px;
      line-height: 28px;
    }
    h5 {
      font-size: 16px;
      line-height: 26px;
    }
  }
}

.blog-footer {
  border-top: 1px solid var(--blog-border-color, $grey-4);
}

.footer-link {
  color: var(--blog-font-color, $grey-4);

  &:hover {
    color: var(--blog-primary-color, $grey-4);
  }
}

:deep(.blog-header-menu) {
  background: var(--blog-card-color, #fff);
  position: sticky;
  top: 16px;
}

:deep(.anchor-list) {
  max-width: 200px;
  list-style-type: none;
  padding-left: 0;
}
:deep(.anchor-item) {
  line-height: 23px;
  color: var(--blog-font-color, $grey-4);
  opacity: 0.5;
  border-left: 1px solid var(--blog-font-color, $grey-4);

  &:hover {
    color: var(--blog-primary-color, $primary);
    opacity: 1;
    border-left: 1px solid var(--blog-primary-color, $primary);
  }
}
.return-button {
  background: var(--blog-card-color, #fff);
  color: var(--blog-font-color, $grey-4);
  opacity: 0.5;
  position: fixed;
  bottom: 130px;
  left: 28px;

  &.for-big-screen {
    $container-width: 1600px;
    $button-width: 62px;
    $footer-logo-width: 70px;
    $footer-padding-left: 24px;
    left: calc(((100vw - $container-width) / 2 ) + $footer-padding-left + (($footer-logo-width - $button-width) / 2));
  }

  &:hover {
    color: var(--blog-primary-color, $primary);
    opacity: 1;
  }

}
:deep(.blog-header-mobile) {
  background: var(--blog-background-color, $grey-1);
  z-index: 1;
  position: sticky;
  top: 0;
  left: 0;
}
:deep(.mobile-menu) {
  background: var(--blog-card-color, #fff);
  z-index: 2;

  .header-link {
    color: var(--blog-font-color, $grey-4);
    opacity: 1;
    font-weight: 500;
    line-height: 24px;
    padding-left: 0;

    &.active {
      color: var(--blog-primary-color, $primary);
      opacity: 1;

      &:after {
        display: none;
      }
    }
  }
}
.blog-footer-mobile {
  position: relative;
}
.footer-link-mobile {
  opacity: 0.5;
}
:deep(.anchors-area) {
  position: sticky;
  top: 90px;
  background: var(--blog-background-color, $grey-1);
  z-index: 2;

  .q-expansion-item__container {
    background: var(--blog-background-color, $grey-1);

    .q-item-type {
      padding: 8px 24px;
    }
  }

  .q-expansion-item__toggle-icon {
    color: var(--blog-icon-color, $grey);
    width: 16px;
  }
}
:deep(.anchor-list-mobile) {
  max-width: unset;
  margin: 0;
  padding-left: 24px;
}
:deep(.anchor-item-mobile) {
  white-space: nowrap;
  font-size: 12px;
  line-height: 22px;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.footer-watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
